import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { Refresh } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../i18n/LanguageContext';
import { Camera } from '../types';
import { getCameras, getStreamUrl, getThumbnailUrl, ptzMove, PtzAction } from '../frigateApi';
import { CameraPlayer } from './CameraPlayer';
import { CameraGrid } from './CameraGrid';
import { PtzControls } from './PtzControls';
import { RecordingsDialog } from './RecordingsDialog';

const MAX_WIDTH = 600;

const RECORD_DURATION_KEY = 'recordDuration';
const DEFAULT_RECORD_DURATION = 10;

export const CamerasPage: React.FC = () => {
  const { t } = useLanguage();
  const { gatewayUrl } = useAuth();
  const [cameras, setCameras] = useState<Camera[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Camera | null>(null);
  const [recordingsFor, setRecordingsFor] = useState<Camera | null>(null);
  const [overrideStreamUrl, setOverrideStreamUrl] = useState<string | null>(null);
  const [recordDuration, setRecordDuration] = useState<number>(() => {
    const stored = localStorage.getItem(RECORD_DURATION_KEY);
    return stored ? Math.max(1, parseInt(stored) || DEFAULT_RECORD_DURATION) : DEFAULT_RECORD_DURATION;
  });

  const load = useCallback(async () => {
    if (!gatewayUrl) return;
    setLoading(true);
    setError(null);
    try {
      const list = await getCameras(gatewayUrl);
      setCameras(list);
      setSelected(prev =>
        prev ? (list.find(c => c.name === prev.name) ?? list[0] ?? null) : (list[0] ?? null),
      );
    } catch (e: any) {
      setError(e.message ?? 'Error');
    } finally {
      setLoading(false);
    }
  }, [gatewayUrl]);

  useEffect(() => { load(); }, [load]);

  const handleCameraSelect = (cam: Camera) => {
    setSelected(cam);
    setOverrideStreamUrl(null);
  };

  const handleCameraUpdate = (updated: Camera) => {
    setCameras(prev => prev.map(c => c.name === updated.name ? updated : c));
    if (selected?.name === updated.name) setSelected(updated);
  };

  const handleRecordDurationChange = (val: number) => {
    setRecordDuration(val);
    localStorage.setItem(RECORD_DURATION_KEY, String(val));
  };

  const isPtz = !!(selected?.config?.onvif?.host || selected?.config?.ptz);
  const liveUrl   = selected && gatewayUrl ? getStreamUrl(gatewayUrl, selected.name) : '';
  const streamUrl = overrideStreamUrl ?? liveUrl;
  const posterUrl = selected && gatewayUrl ? getThumbnailUrl(gatewayUrl, selected.name) : '';

  const handlePtz = async (action: PtzAction) => {
    if (!selected || !gatewayUrl) return;
    try { await ptzMove(gatewayUrl, selected.name, action); } catch { /* silent */ }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ textAlign: 'center', pt: 8, px: 2 }}>
        <Typography color="error" gutterBottom>{t.errorConnection}</Typography>
        <Tooltip title={t.camerasRefresh}>
          <IconButton aria-label={t.camerasRefresh} onClick={load}><Refresh /></IconButton>
        </Tooltip>
      </Box>
    );
  }

  if (cameras.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', pt: 8, px: 2 }}>
        <Typography color="text.secondary">{t.camerasNone}</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        maxWidth: MAX_WIDTH,
        mx: 'auto',
        width: '100%',
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      {selected && streamUrl && (
        <CameraPlayer
          src={streamUrl}
          poster={posterUrl}
          controls={!!overrideStreamUrl}
        />
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {isPtz && (
          <Box sx={{ flex: 1 }}>
            <PtzControls onMove={handlePtz} />
          </Box>
        )}
        <Box sx={{ ml: 'auto' }}>
          <Tooltip title={t.camerasRefresh}>
            <IconButton size="small" aria-label={t.camerasRefresh} onClick={load}><Refresh /></IconButton>
          </Tooltip>
        </Box>
      </Box>

      <CameraGrid
        cameras={cameras}
        selected={selected}
        gatewayUrl={gatewayUrl ?? ''}
        recordDuration={recordDuration}
        onSelect={handleCameraSelect}
        onOpenRecordings={cam => setRecordingsFor(cam)}
        onUpdate={handleCameraUpdate}
        onRecordDurationChange={handleRecordDurationChange}
      />

      <RecordingsDialog
        camera={recordingsFor}
        gatewayUrl={gatewayUrl ?? ''}
        open={recordingsFor !== null}
        onClose={() => setRecordingsFor(null)}
        onPlay={url => {
          setOverrideStreamUrl(url);
          setRecordingsFor(null);
        }}
      />
    </Box>
  );
};
